import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle, TrendingUp, CircleCheck, Sparkles, ArrowRight, Rocket, RotateCcw,
  MousePointerClick, Users, Target, Inbox,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fetchOverview } from "@/lib/api";

/**
 * Priority levels.
 *
 * One treatment per level, not four. The previous card carried a coloured left
 * border, a chip, a coloured dot inside the chip and a separate "Start here"
 * pill simultaneously, so the eye had to reconcile four signals to read one
 * state.
 */
const LEVEL = {
  unavailable: { label: "Data unavailable", badge: "info", rule: "bg-line-strong", Icon: AlertTriangle },
  high: { label: "Needs attention", badge: "attention", rule: "bg-amber-500", Icon: AlertTriangle },
  medium: { label: "Worth a look", badge: "attention", rule: "bg-amber-500/50", Icon: TrendingUp },
  low: { label: "Healthy", badge: "positive", rule: "bg-blue-600", Icon: CircleCheck },
  new: { label: "New", badge: "info", rule: "bg-line-strong", Icon: Rocket },
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-1.5 text-slate-600">
    <Icon size={14} aria-hidden="true" />
    <span className="tabular font-semibold text-navy-950">{value}</span>
    <span className="text-caption">{label}</span>
  </div>
);

export default function Overview({ range, onOpenSite, onConnect }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchOverview(range)
      .then((d) => { if (!cancelled) setSites(d.sites || []); })
      .catch(() => { if (!cancelled) { setSites([]); setError(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, attempt]);

  const topPriority = sites.find((s) => s.level === "high" || s.level === "medium");

  return (
    <section data-testid="overview-screen">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 text-eyebrow uppercase text-emerald-700">
          <Sparkles size={14} aria-hidden="true" /> Across all your sites
        </span>
        <h1 className="mt-2 font-heading text-h1-sm lg:text-h1-app text-navy-950">
          Which site needs you first
        </h1>
        <p className="mt-3 text-body text-slate-600">
          {loading ? "Scanning every connected site…"
            : error ? "We couldn't load your sites just now."
            : topPriority
              ? <>Start with <span className="font-semibold text-navy-950">{topPriority.display_name}</span> — it has the most pressing issues right now.</>
              : sites.length === 0
                ? "No connected sites yet."
                : sites.some(s => s.level === "unavailable") ? "Some Google data is unavailable. Open a site to check its connection." : "Everything's looking healthy across your sites. Nice work."}
        </p>
      </div>

      {!loading && !error && sites.length === 0 && <Button onClick={onConnect} className="mt-4">Connect Google</Button>}
      <div className="mt-8 grid gap-4">
        {loading ? (
          // Skeletons match the final card height so the page does not jump.
          [0, 1, 2].map((i) => (
            <div key={i} data-testid="overview-skeleton" className="h-[152px] rounded-card border border-line bg-white animate-pulse" />
          ))
        ) : error ? (
          <div data-testid="overview-error" className="rounded-card border border-line bg-white p-8 text-center">
            <AlertTriangle size={24} className="mx-auto text-amber-700" aria-hidden="true" />
            <p className="mt-3 text-body text-slate-600">
              We couldn't reach your site list. Nothing is lost — try again in a moment.
            </p>
            <Button variant="quiet" size="control" onClick={() => setAttempt((a) => a + 1)} className="mt-4" data-testid="overview-retry">
              <RotateCcw size={15} /> Try again
            </Button>
          </div>
        ) : sites.length === 0 ? (
          <div data-testid="overview-empty" className="rounded-card border border-line bg-white p-8 text-center">
            <Inbox size={24} className="mx-auto text-slate-500" aria-hidden="true" />
            <p className="mt-3 text-body text-slate-600">
              No sites are connected yet. Connect a Google account and your Search Console
              properties will appear here.
            </p>
          </div>
        ) : (
          sites.map((s, i) => {
            const cfg = LEVEL[s.level] || LEVEL.new;
            const { Icon } = cfg;
            const startHere = i === 0 && (s.level === "high" || s.level === "medium");
            return (
              <motion.button
                key={s.id}
                initial={reduceMotion ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => onOpenSite(s.id)}
                data-testid={`overview-site-${i}`}
                // Hover changes the border, never the position. A card that
                // moves under the cursor makes a list harder to scan, not easier.
                className="group relative w-full overflow-hidden rounded-card border border-line bg-white p-5 text-left transition-colors duration-card ease-editorial hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite md:p-6"
              >
                <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${cfg.rule}`} />
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <Icon size={18} className="mt-1 shrink-0 text-slate-600" aria-hidden="true" />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-heading text-h3 text-navy-950">{s.display_name}</h3>
                        {startHere && <Badge variant="new">Start here</Badge>}
                      </div>
                      <p className="mt-1 text-small text-slate-600">{s.headline}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                        <Metric icon={MousePointerClick} label="clicks" value={(s.metrics.clicks?.toLocaleString() ?? "—")} />
                        <Metric icon={Users} label="users" value={(s.metrics.users?.toLocaleString() ?? "—")} />
                        <Metric icon={Target} label="key events" value={(s.metrics.conversions?.toLocaleString() ?? "—")} />
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-3">
                    <Badge variant={cfg.badge}>{cfg.label}</Badge>
                    <span className="inline-flex items-center gap-1 text-small font-semibold text-emerald-700">
                      Open <ArrowRight size={15} aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </motion.button>
            );
          })
        )}
      </div>
    </section>
  );
}
